import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShareService } from '@/services/ShareService';
import { BudgetService } from '@/services/BudgetService';
import { budgetKeys } from '@/hooks/useBudgets';
import type { CreateShareRequest, ShareRole, Budget } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

export const shareKeys = {
  all: ['shares'] as const,
  invitations: () => [...shareKeys.all, 'invitations'] as const,
  forBudget: (budgetId: string) => [...shareKeys.all, 'budget', budgetId] as const,
};

export function useShareInvitations() {
  return useQuery({
    queryKey: shareKeys.invitations(),
    queryFn: () => ShareService.getInvitations(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useBudgetShares(budgetId: string | undefined) {
  return useQuery({
    queryKey: shareKeys.forBudget(budgetId ?? ''),
    queryFn: () => ShareService.getSharesForBudget(budgetId!),
    enabled: !!budgetId,
    staleTime: 30 * 1000,
  });
}

export function useInviteToShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateShareRequest) => ShareService.invite(request),
    onSuccess: (share) => {
      queryClient.invalidateQueries({ queryKey: shareKeys.forBudget(share.budgetId) });
      queryClient.invalidateQueries({ queryKey: shareKeys.invitations() });
    },
  });
}

export function useAcceptShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shareId: string) => ShareService.accept(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shareKeys.invitations() });
      queryClient.invalidateQueries({ queryKey: shareKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.activeSummaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.active() });
    },
  });
}

export function useRejectShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shareId: string) => ShareService.reject(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shareKeys.invitations() });
    },
  });
}

export function useRevokeShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shareId: string) => ShareService.revoke(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shareKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

/**
 * Leave a budget that was shared with the current user
 * (removes the user's own accepted share).
 */
export function useLeaveSharedBudget() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (budgetId: string) => {
      const email = user?.email?.toLowerCase() ?? '';
      // Share records live on the invited ancestor (usually the root). Walk up
      // until we find the collaborator's accepted share.
      let currentId: string | undefined = budgetId;
      const visited = new Set<string>();
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const shares = await ShareService.getSharesForBudget(currentId);
        const myShare = shares.find(
          (share) => share.accepted && share.userEmail?.toLowerCase() === email
        );
        if (myShare) {
          await ShareService.revoke(myShare.id);
          return;
        }
        const budget = await BudgetService.getById(currentId);
        currentId = budget?.parentId ?? undefined;
      }
      throw new Error('Could not find your share for this budget');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shareKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
    },
  });
}

export function useUpdateShareRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ shareId, role }: { shareId: string; role: ShareRole }) =>
      ShareService.updateRole(shareId, role),
    onSuccess: (share) => {
      queryClient.invalidateQueries({ queryKey: shareKeys.forBudget(share.budgetId) });
    },
  });
}

export function useShareUtils() {
  return {
    validateCreateRequest: ShareService.validateCreateRequest,
    getRoleName: ShareService.getRoleName,
    canEdit: ShareService.canEdit,
    canManageShares: ShareService.canManageShares,
    canDelete: ShareService.canDelete,
  };
}

export function useBudgetPermissions(budgetId: string | undefined, budget?: Budget | null) {
  const { user } = useAuth();
  const { data: shares = [] } = useBudgetShares(budgetId);

  const isOwner =
    budget?.owned ??
    (budget ? budget.ownerId === user?.id : shares.every((s) => !s.accepted || !s.userId));

  const myShare = shares.find((s) => s.accepted);
  const role: ShareRole | undefined = isOwner ? 'OWNER' : myShare?.role;
  const canEdit = budget?.canEdit ?? (role ? ShareService.canEdit(role) : false);

  return {
    role,
    isOwner,
    canEdit,
    /** Root budgets the user can view may be copied into their own account. */
    canCopy: !budget?.parentId,
    canManageShares: isOwner,
    canDelete: isOwner,
  };
}

export function usePendingInvitations() {
  const { data: invitations = [], ...rest } = useShareInvitations();

  return {
    ...rest,
    pendingInvitations: invitations,
    pendingCount: invitations.length,
  };
}
